import React, { useContext, useEffect, useState } from 'react'
import { Form, Input, Spin, Select, App, Radio } from "antd"
import { DatePicker } from "antd-jalali"
import {Dayjs} from "dayjs";
import { Regex, API, Enum } from "../../../resources/constants";
import language from "../../../resources/languages/fa_IR"

// Contexts
import AxiosContext from "../../../contexts/axios";

// Interfaces
import { AxiosError } from "../../../interfaces/axios"
import { AntTableParams } from "../../../interfaces/ant"
import type { TablePaginationConfig } from 'antd/es/table'

interface ProvincesType {
    id: string,
    name: string,
    cities: CityType[],
}

interface ProvinceType {
    id: string,
    name: string,
    publish: boolean,
    createdAt: Date,
    updatedAt: Date,
}

interface CityType {
    id: string,
    name: string,
    province: ProvinceType,
    publish: boolean,
    createdAt: Date,
    updatedAt: Date,
}

interface UserType {
    id: string,
    firstName: string,
    lastName: string,
    nationalCode: string,
    mobile: string,
    email?: string,
    createdAt: Date,
    updatedAt: Date,
}

interface CompanyType {
    row: number,
    id: string,
    key: string,
    number: string,
    position: number,
    name: string,
    tel: string,
    cities: CityType[],
    user: UserType,
    publish: boolean,
    tickets?: { count: number, seat: number }[]
    seatOne?: number,
    seatTwo?: number,
    seatThree?: number,
    seatLeft?: number,
    carLeft?: number,
    countService?: number,
    priority?: number,
    createdAt: Date,
    updatedAt: Date,
}

interface FiltersType {
    number?: string,
    firstName?: string,
    lastName?: string,
    mobile?: string,
    nationalCode?: string,
    destination?: string,
    company?: string,
    createdAt?: Dayjs,
    updatedAt?: Dayjs,
}

const { Pages: { TicketManagement: { Ticket: TicketLanguage } }, Enum: EnumLanguage, Share, Validation, Message: MessageLanguage } = language
const { BASIC_INFORMATION_PROVINCE_CITIES, TICKET_MANAGEMENT_COMPANY_ALL } = API;
const { isNumber, notEnglish } = Regex;

export default function Filter(props: { Form: any, form: any, filters: FiltersType, setIsModalOpen: (isModalOpen: boolean) => void, getTickets: (filters: FiltersType, params: AntTableParams) => void, pagination: TablePaginationConfig }) {
    const { Form, form, filters, setIsModalOpen, getTickets, pagination } = props

    const { axios } = useContext(AxiosContext)
    const [loadingProvinces, setLoadingProvinces] = useState<boolean>(false)
    const [provinces, setProvinces] = useState<ProvincesType[]>([])
    const [loadingCompanies, setLoadingCompanies] = useState<boolean>(false)
    const [companies, setCompanies] = useState<CompanyType[]>([])
    const { message } = App.useApp();

    useEffect(() => {
        form.resetFields()
        initialAPI().then(response => response)
    }, [])


    const initialAPI = async () => {
        await getCompanies();
        await getProvinces();
    }

    const getCompanies = async () => {
        setLoadingCompanies(true);
        await axios.post(TICKET_MANAGEMENT_COMPANY_ALL, { publish: true }).then((response: { data: CompanyType[] }) => {
            const { data: companies } = response
            setCompanies(companies)
        }).catch((error: AxiosError) => {
            const { response: { data } } = error;
            // @ts-ignore
            message.error(MessageLanguage[data.message]).then(response => response )
            setCompanies([])
        });
        setLoadingCompanies(false);
    }

    const getProvinces = async () => {
        setLoadingProvinces(true)
        await axios.get(BASIC_INFORMATION_PROVINCE_CITIES).then((response: { data: ProvincesType[] }) => {
            const { data: provinces } = response
            setProvinces(provinces)
        }).catch((error: AxiosError) => {
            const { response: { data } } = error;
            // @ts-ignore
            message.error(MessageLanguage[data.message]).then(response => response )
            setProvinces([])
        })
        setLoadingProvinces(false)
    }

    const onFinish = ((filters: FiltersType) => {
        const { current, pageSize } = pagination
        getTickets({ ...filters,
            number: filters.number || undefined,
            firstName: filters.firstName || undefined,
            lastName: filters.lastName || undefined,
            mobile: filters.mobile?.substring(1) || undefined,
            nationalCode: filters.nationalCode || undefined,
            destination: filters.destination || undefined,
            company: filters.company || undefined,
            createdAt: filters.createdAt || undefined,
            updatedAt: filters.updatedAt || undefined,
        }, { page: 1, limit: pageSize })
        setIsModalOpen(false)
    })

    return (
        <Spin spinning={loadingProvinces || loadingCompanies} tip={Share.loading} size="large">
            <Form form={form} name="filter" onFinish={onFinish} initialValues={filters} style={{ margin: '25px 0 10px 0'}}>
                <Form.Item
                    label={TicketLanguage.number}
                    name="number"
                    hasFeedback
                    rules={[
                        { pattern: isNumber, message: `${TicketLanguage.number} ${Validation.pattern}` },
                        { len: 6, message: `${TicketLanguage.number} 6 ${Validation.length}` },
                    ]}
                >
                    <Input type="tel" maxLength={6} />
                </Form.Item>
                <Form.Item
                    label={TicketLanguage.mobile}
                    name="mobile"
                    hasFeedback
                    rules={[
                        { pattern: isNumber, message: `${TicketLanguage.mobile} ${Validation.pattern}` },
                        { len: 11, message: `${TicketLanguage.mobile} 11 ${Validation.length}` },
                    ]}
                >
                    <Input
                        type="tel"
                        maxLength={11}
                        placeholder={TicketLanguage.mobile}
                    />
                </Form.Item>
                <Form.Item
                    label={TicketLanguage.firstName}
                    name="firstName"
                    hasFeedback
                    rules={[
                        { min: 3, message: `${TicketLanguage.firstName} ${Validation.min} 3 ${Validation.length}` },
                        { max: 30, message: `${TicketLanguage.firstName} ${Validation.max} 30 ${Validation.length}` },
                        { pattern: notEnglish, message: Validation.persian },
                    ]}
                >
                    <Input maxLength={30} placeholder={TicketLanguage.firstName} />
                </Form.Item>
                <Form.Item
                    label={TicketLanguage.lastName}
                    name="lastName"
                    hasFeedback
                    rules={[
                        { min: 3, message: `${TicketLanguage.lastName} ${Validation.min} 3 ${Validation.length}` },
                        { max: 30, message: `${TicketLanguage.lastName} ${Validation.max} 30 ${Validation.length}` },
                        { pattern: notEnglish, message: Validation.persian },
                    ]}
                >
                    <Input maxLength={30} placeholder={TicketLanguage.lastName} />
                </Form.Item>
                <Form.Item
                    label={TicketLanguage.nationalCode}
                    name="nationalCode"
                    hasFeedback
                    rules={[
                        { pattern: isNumber, message: `${TicketLanguage.nationalCode} ${Validation.pattern}` },
                        { len: 10, message: `${TicketLanguage.nationalCode} 10 ${Validation.length}` },
                    ]}
                >
                    <Input type="tel" maxLength={10} placeholder={TicketLanguage.nationalCode} />
                </Form.Item>
                <Form.Item
                    label={TicketLanguage.company}
                    name="company"
                    hasFeedback
                    rules={[]}
                >
                    <Select
                        showSearch
                        allowClear
                        optionFilterProp="label"
                        placeholder={Share.pleaseSelectedRecord}
                        options={companies.map(({ id, name}) => ({ label: name, value: id}))}
                    />
                </Form.Item>
                <Form.Item
                    label={TicketLanguage.destination}
                    name="destination"
                    hasFeedback
                    rules={[]}
                >
                    <Select
                        showSearch
                        allowClear
                        placeholder={Share.pleaseSelectedRecords}
                        optionFilterProp="name"
                        options={provinces.map(({ name, cities }) => ({ label: name, options: cities.map(({ id, name }) => ({ label: name, value: id, name }))}))}
                    />
                </Form.Item>
                <Form.Item
                    label={TicketLanguage.createdAt}
                    name="createdAt"
                    hasFeedback
                >
                    <DatePicker.RangePicker
                        allowClear
                        style={{ width: '100%' }}
                        showTime={{ format: 'HH:mm' }}
                        format="HH:mm YYYY/MM/DD"
                    />
                </Form.Item>
                <Form.Item
                    label={TicketLanguage.updatedAt}
                    name="updatedAt"
                    hasFeedback
                >
                    <DatePicker.RangePicker
                        allowClear
                        style={{ width: '100%' }}
                        showTime={{ format: 'HH:mm' }}
                        format="HH:mm YYYY/MM/DD"
                    />
                </Form.Item>
            </Form>
        </Spin>
    )
}
